<template>
<div>
    <loading v-if="loading"></loading>
    <div id="profile-body" v-if="!loading">
        <div class="row match-height">
            <div class="col-md-6 col-mobile" v-if="profile.isClub">
                <section class="card">
                    <h1>{{ t.club.club_profile }}</h1>
                    <p>{{ t.club.attached_profile }}</p>
                    <p v-html="t.club.club_redirect"></p>
                    <br>
                    <p>{{ revertLink[0] }} <a href="#" @click.prevent="revertClubProfile">{{ revertLink[1] }}</a>.</p>
                </section>
            </div>
            <div :class="profile.persons.length === 2 || extendedPerson ? 'col-md-4 col-mobile' : 'col-md-6 col-mobile'">
                <profile-info :t="t" :profile="profile" :local-config="localConfig" :edit-mode="editMode"></profile-info>
            </div>
            <profile-stats v-if="!profile.isClub" v-for="(person, index) in profile.persons" :key="person.id" :edit-mode="editMode"
                :person="person" :profile="profile" :extended-person="extendedPerson" :index="index" :local-config="localConfig"
                :t="t"></profile-stats>
            <profile-stats-extended v-if="!profile.isClub && extendedPerson" :profile="profile" :local-config="localConfig" :t="t"
                :edit-mode="editMode"></profile-stats-extended>
        </div>
        <div class="row">
            <div class="profile-latest-photos profile-photos col-xs-12 col-mobile" v-if="latestMedia.length > 2">
                <media v-for="(media, index) in latestMedia" :key="media.id" @click.native="showMediaViewer(index)" :class="{ 'show-join': loggedOut }" :media="media"></media>
            </div>
            <div class="col-xs-12 col-mobile">
                <profile-description v-if="!profile.isClub && (editMode || profile.description)" :local-config="localConfig"
                    :profile="profile" :t="t" :edit-mode="editMode"></profile-description>
            </div>
            <div class="col-xs-12 col-mobile">
                <section class="card">
                    <button class="btn btn-primary pull-right"
                        @click.prevent="$root.showVouchModal = true"
                        v-if="profile.isFriend && $root.showVouchButton && !profile.isVouched"
                    >
                        {{ t.info.vouch }}
                    </button>
                    <a :href="localConfig.pages.vouch"><h1>{{ t.info.num_vouches }}</h1></a>
                    <router-link v-if="!localConfig.data.numVouches && owner" tag="div" :to="{ name: 'friends' }">
                        <a class="btn btn-default">{{ t.info.vouch_suggest }}</a>
                    </router-link>
                    <div class="row">
                        <div class="profile-vouch clearfix col-md-4" v-for="vouch in localConfig.data.vouches" :key="vouch.id">
                            <Avatar :user="vouch.user" size="medium" v-miniprofile="vouch.user.id"></Avatar>
                            <div class="profile-vouch-text">{{ vouch.text }}</div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
        <div class="row" :class="{'match-height': !localConfig.data.showComments }" v-if="!profile.isClub">
            <template v-if="localConfig.data.showComments">
                <div class="col-md-6 col-mobile">
                    <profile-seeking :t="t" :profile="profile" :local-config="localConfig" :edit-mode="editMode"></profile-seeking>
                    <profile-interests :t="t" :profile="profile" :local-config="localConfig" :edit-mode="editMode"></profile-interests>
                </div>
                <section class="col-md-6 col-mobile">
                    <div class="profile-comments-wrapper card">
                        <h1>{{ t.user_view.comments }}</h1>
                        <comments :show-form="true" type="profile_comment" :id="profile.profileId"></comments>
                    </div>
                </section>
            </template>
            <template v-else>
                <div class="col-md-6 col-mobile">
                    <profile-seeking :t="t" :profile="profile" :local-config="localConfig" :edit-mode="editMode"></profile-seeking>
                </div>
                <div class="col-md-6 col-mobile">
                    <profile-interests :t="t" :profile="profile" :local-config="localConfig" :edit-mode="editMode"></profile-interests>
                </div>
            </template>
        </div>
    </div>
    <modal v-model="showEmailVerify" :title="t.email_verify_title" :hide-footer="true" :closable="false">
        <email-verify v-if="showEmailVerify" super-blur="profile-body"></email-verify>
    </modal>
</div>
</template>

<script>
import ajax from 'Site/js/util/ajax.fetch';
import miniprofileDirective from '../directive/miniprofile';
import { mapState } from 'vuex';
import Avatar from 'Profile/component/Avatar.vue';
import Comments from 'Feedback/component/Comments.vue';
import EmailVerifyModal from 'User/js/mixin/email.verify.modal';
import Loading from 'Site/component/Loading.vue';
import Media from 'Media/component/Media.vue';
import ProfileDescription from 'Profile/component/ProfileDescription.vue';
import ProfileInfo from 'Profile/component/ProfileInfo.vue';
import ProfileInterests from 'Profile/component/ProfileInterests.vue';
import ProfileSeeking from 'Profile/component/ProfileSeeking.vue';
import ProfileStats from 'Profile/component/ProfileStats.vue';
import ProfileStatsExtended from 'Profile/component/ProfileStatsExtended.vue';

export default {
    name: 'Profile',
    mixins: [EmailVerifyModal],
    components: {
        Avatar,
        Comments,
        Loading,
        Media,
        ProfileDescription,
        ProfileInfo,
        ProfileInterests,
        ProfileSeeking,
        ProfileStats,
        ProfileStatsExtended,
    },
    computed: {
        ...mapState({
            notEmailVerified: state => state.config.data.verifyId !== null,
            owner: state => state.config.data.owner,
            profile: state => state.config.data.profile
        }),
        editMode() {
            return this.$root.editMode;
        },
        extendedPerson() { return this.localConfig.data ? this.localConfig.data.extendedPerson : false; },
        latestMedia() {
            return this.localConfig.data ? this.localConfig.data.latestMedia || [] : {};
        },
        revertLink() {
            return this.t.club ? this.t.club.revert_club_profile.replace('</a>', '').split('<a href="%url">') : [];
        },
        t() {
            return this.localConfig.translate ? this.localConfig.translate.profile : {};
        }
    },
    data() {
        return {
            loading: true,
            localConfig: {},
            loggedOut: this.$store.state.config.loggedOut || false
        };
    },
    directives: {
        miniprofile: miniprofileDirective
    },
    methods: {
        revertClubProfile() {
            ajax.update('profile', {
                id: this.profile.id,
                club: null
            }).then((response) => {
                this.$store.dispatch('notify', { msg: response.message });
                window.location.reload();
            });
        },
        showMediaViewer(start) {
            if (!this.loggedOut) {
                this.$store.dispatch('showMediaViewer', {
                    media: this.latestMedia,
                    start
                });
            }
        }
    },
    created() {
        if (document.querySelector('#user-view-json')) {
            const settings = document.querySelector('#user-view-json').textContent;
            if (settings) {
                this.localConfig = JSON.parse(settings);
                this.loading = false;
            }
        } else {
            ajax.get(window.location.href).then((data) => {
                this.localConfig = JSON.parse(data);
                this.loading = false;
            });
        }
    },
    mounted() {
        if (this.notEmailVerified && !this.owner) {
            this.showEmailVerify = true;
        }
    }
};

</script>
